import { toRefs } from '@vueuse/shared';
import type { UseCustomerReturn, UseCustomerState, FetchCustomer } from '~/composables/useCustomer/types';
import { useSdk } from '~/sdk';

/**
 * @description Composable managing customer data
 * @returns {@link UseCustomerReturn}
 * @example
 * const { data, loading, fetchCustomer } = useCustomer();
 */
export const useCustomer: UseCustomerReturn = () => {
  const state = useState<UseCustomerState>('useCustomer', () => ({
    data: null,
    loading: false,
  }));

  /** Function for fetching customer data
   * @example
   * fetchCustomer();
   */
  const fetchCustomer: FetchCustomer = async () => {
    const { user: userData, clear: clearSession} = useUserSession()
    state.value.loading = true;
    const { data, error } = await useAsyncData(() => useSdk().magento.customer(userData.value?.token));
    useHandleError(error.value);
    state.value.data = data;
    if(!data.value?.email) {
      await clearSession();
    }
    state.value.loading = false;
    return data;
  };

  const getCustomerAttachments = async () => {
    const { user: userData} = useUserSession()
    if (!userData.value?.token) {
      console.warn('No user token found, cannot fetch customer attachments.');
      return [];
    }

    return await useSdk().magento.customerAttachments(userData.value?.token);
  };

  const getCustomerTeam = async () => {
    const { user: userData} = useUserSession()
    if (!userData.value?.token) {
      console.warn('No user token found, cannot fetch customer team.');
      return [];
    }

    return await useSdk().magento.customerTeam(userData.value?.token);
  };

  const updateCustomerBilling = async (params: any) => {
    const { user: userData} = useUserSession()
    if (!userData.value?.token) {
      console.warn('No user token found, cannot update customer billing.');
      return [];
    }

    return await useSdk().magento.updateCustomerBilling(userData.value?.token, params);
  };

  return {
    fetchCustomer,
    getCustomerAttachments,
    getCustomerTeam,
    updateCustomerBilling,
    ...toRefs(state.value),
  };
};
